/* eslint-disable no-param-reassign, max-params, max-lines */
import { api } from "jiffy-api";
import debounce from "lodash.debounce";
import getMetaEnv from "common/utils/meta";
import { camelToSnakeCase, camelize } from "common/utils/helpers/cases";
import { getBusinessLinePath } from "components/checkout_v2/checkout-config";
import { sequentialTaskQueue } from "components/pod/pod-build-order/utils/sequential-task-queue";
import { EVENTS, trackPlacementEvent } from "../utils/tracker";

const initialState = {
  csrfToken: getMetaEnv("csrf-token"),
  currentOrder: undefined,
  paypalOrderCreateData: {
    coupon_code: "",
    use_store_credits: false,
    order_type: getBusinessLinePath(),
    authenticity_token: getMetaEnv("csrf-token")
  },
  loading: {
    expressCheckout: false
  },
  pageSource: "Order Builder",
  /* List of compositions for preview, but which doesn't have quantity  */
  previewCompositions: {},
  updatePlacementAbortSignal: null,
  currentPlacementAction: null
};

const checkoutModule = {
  namespaced: true,

  state() {
    return { ...initialState };
  },

  getters: {
    currentAssemblyGroup: state => state.currentOrder?.print_assembly_groups[0], // TODO: hardcoded
    compositions: (_, getters) => getters.currentAssemblyGroup.compositions,
    previewCompositions: state => state.previewCompositions,
    placements: (_, getters) => getters.currentAssemblyGroup?.placements || [],
    currentPlacement: (_, getters) => getters.placements[0], // TODO: hardcoded
    totals: state => {
      if (!state.currentOrder) return null;

      return {
        bulkDiscount: Number(state.currentOrder.bulk_discount),
        createdProduct: Number(state.currentOrder.product_total),
        grand: Number(state.currentOrder.total_without_tax),
        shipping: Number(state.currentOrder.shipping_total),
        promotionTotal: Number(state.currentOrder.promotion_total || 0)
      };
    },
    expressCheckoutLoading: state => state.loading.expressCheckout,
    paypalOrderCreateData: state => state.paypalOrderCreateData,
    pageSource: state => state.pageSource,
    userType: state => (state.currentOrder.isGuest ? "guest" : "user"),
    orderNumber: state => state.currentOrder.number,
    currency: state => state.currentOrder.currency,
    locale: state => state.currentOrder.locale,
    csrfToken: state => state.csrfToken,
    countryCode: state => state.currentOrder.countryCode,
    publishableKey: state => state.currentOrder.publishableKey,
    stripeLineItems: state => state.currentOrder.stripeLineItems,
    stripeShippingRates: state => state.currentOrder.stripeShippingRates,
    totalAmountInCents: state => state.currentOrder.totalAmountInCents,
    bulkDiscount: (_, getters) => getters.totals?.bulkDiscount || 0,
    currentPlacementAction: state => state.currentPlacementAction,
    couponCode: state => state.currentOrder?.coupon,
    placementPayload: (_, getters, rootState, rootGetters) => options => {
      const { width, height } = rootGetters["artwork/artworkSize"];

      return {
        print_assembly_group_id: getters.currentAssemblyGroup.id,
        placement: {
          enhance: rootGetters["artwork/enhanced"],
          comment: rootGetters["artwork/comment"],
          remove_background: rootGetters["artwork/removeBackground"],
          position: camelToSnakeCase(rootGetters["artwork/surfaceLabel"]),
          height: rootGetters["artwork/convertPxToInches"](height),
          width: rootGetters["artwork/convertPxToInches"](width),
          ...options
        }
      };
    }
  },

  mutations: {
    toggleLoading(state, [key, value]) {
      state.loading[key] = value;
    },
    setPreviewComposition(state, composition) {
      state.previewCompositions[`${composition.id}`] = composition;
    },
    setCurrentPlacementAction(state, action) {
      state.currentPlacementAction = action;
    },
    abortUpdatePlacement(state) {
      state.updatePlacementAbortSignal?.abort();
      state.updatePlacementAbortSignal = null;
    }
  },

  actions: {
    async restoreOrder({ getters, commit, dispatch }) {
      await dispatch("getCurrentOrder");

      const { placements, currentAssemblyGroup } = getters;
      const [firstPlacement] = placements;

      if (firstPlacement) {
        const { width, height, position, dtf_artwork: artwork } = firstPlacement;
        commit("artwork/setSurface", camelize(position), { root: true });
        commit("artwork/setArtwork", artwork, { root: true });
        commit("artwork/setEnhancePreselected", true, { root: true });
        commit("artwork/setArtworkSizeInches", { width, height }, { root: true });
      }

      if (currentAssemblyGroup) {
        commit("product/setOrderData", currentAssemblyGroup, { root: true });
        dispatch("product/fetchAllCompositionsPrices", null, { root: true });
      }

      commit("setCurrentPage", "builder", { root: true });
    },

    async getCurrentOrder({ state, commit }) {
      commit("loading/setCurrentOrder", true, { root: true });
      state.currentOrder = await api.printOnDemand.currentPodOrder();
      commit("loading/setCurrentOrder", false, { root: true });
    },

    debouncedUpdateOrder: debounce(async ({ state, getters, commit, dispatch }, payload) => {
      commit("loading/setCurrentOrder", true, { root: true });

      state.currentOrder = await sequentialTaskQueue.enqueue(() =>
        api.printOnDemand.updatePodOrder({
          print_assembly_group_id: getters.currentAssemblyGroup.id,
          ...payload
        })
      );

      commit("loading/setCurrentOrder", false, { root: true });
      dispatch("delivery/getDelivery", { showErrors: true }, { root: true });
    }, 500),

    async updateComposition({ rootGetters, dispatch }) {
      dispatch("debouncedUpdateOrder", {
        variants_data: rootGetters["product/flatVariantsQty"]
      });
    },

    async createPlacement({ rootGetters, state, getters, commit, dispatch }) {
      commit("loading/setCurrentOrder", true, { root: true });
      try {
        state.currentOrder = await sequentialTaskQueue.enqueue(() =>
          api.printOnDemand.updatePodOrder(
            getters.placementPayload({
              dtf_artwork_id: rootGetters["artwork/artwork"].id
            })
          )
        );
      } catch (e) {
        // DTF Artwork attachment to placement failed or placement creation errored
        // As the user won't be able do anything with artwork for now force reload
        // To do - Remove auto refresh.
        // Show an error message in future asking user to reload page manually
        window.location.reload();
      }
      state.previewCompositions = {};
      dispatch("delivery/getDelivery", null, { root: true });
      commit("loading/setCurrentOrder", false, { root: true });
      dispatch("trackEvent", { eventName: EVENTS.PLACEMENT.CREATE });
    },

    async updatePlacement({ state, getters, commit, dispatch }) {
      if (!getters.currentPlacement) return;
      commit("loading/setCurrentOrder", true, { root: true });
      commit("abortUpdatePlacement");
      commit("creation/abortPreviewUrl", null, { root: true });
      state.updatePlacementAbortSignal = new AbortController();
      const { width, height, position } = getters.currentPlacement;
      state.currentOrder = await sequentialTaskQueue.enqueue(() =>
        api.printOnDemand.updatePodOrder(
          getters.placementPayload({
            id: getters.currentPlacement.id
          }),
          {
            useAxiosForRequest: true,
            signal: state.updatePlacementAbortSignal.signal
          }
        )
      );
      const { width: nWidth, height: nHeight, position: nPosition } = getters.currentPlacement;
      if (nWidth !== width || nHeight !== height || nPosition !== position) {
        state.previewCompositions = {};
        commit("loading/setCurrentOrder", false, { root: true });
        dispatch("delivery/getDelivery", null, { root: true });
        dispatch("trackEvent", {
          eventName: EVENTS.PLACEMENT.UPDATE,
          height,
          width,
          position
        });
      }
    },

    async removePlacement({ state, getters, commit, dispatch }) {
      commit("loading/setCurrentOrder", true, { root: true });
      const placement = getters.currentPlacement;
      if (!placement) return;
      state.loading = true;
      state.currentOrder = await sequentialTaskQueue.enqueue(() =>
        api.printOnDemand.updatePodOrder({
          print_assembly_group_id: getters.currentAssemblyGroup.id,
          placement: {
            id: placement.id,
            _destroy: true
          }
        })
      );
      dispatch("delivery/getDelivery", null, { root: true });
      dispatch("trackEvent", {
        eventName: EVENTS.PLACEMENT.DESTROY,
        destroyedPlacement: placement
      });
      commit("loading/setCurrentOrder", false, { root: true });
    },

    trackEvent({ getters }, { eventName, destroyedPlacement, oldHeight, oldWidth, oldPosition }) {
      const placement = destroyedPlacement || getters.currentPlacement;
      const action = getters.currentPlacementAction;
      if (!placement) return;

      trackPlacementEvent(eventName, placement, { oldHeight, oldWidth, oldPosition, action });
    },

    async applyCouponCode({ state, commit, dispatch }, { coupon }) {
      try {
        commit("loading/setCurrentOrder", true, { root: true });

        state.currentOrder = await sequentialTaskQueue.enqueue(() =>
          api.printOnDemand.updatePodOrder({
            coupon_code: coupon
          })
        );

        commit("loading/setCurrentOrder", false, { root: true });
        dispatch("delivery/getDelivery", null, { root: true });
      } catch (error) {
        commit("loading/setCurrentOrder", false, { root: true });
        const errorMessage = JSON.parse(error.message).error;
        throw new Error(errorMessage);
      }
    }
  }
};

export default checkoutModule;
